import { useState } from "react";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { GitMerge } from "lucide-react";

const Row = ({ label, value, testid }) => (
  <div className="flex justify-between text-sm border-b border-border py-1.5"><span className="text-muted-foreground">{label}</span><span className="text-primary font-medium" data-testid={testid}>{value ?? "—"}</span></div>
);

export const MergeCustomersDialog = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [a, setA] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const analyze = async () => {
    setBusy(true); setRes(null);
    try { const r = await api.get("/customers/merge/analyze"); setA(r.data); setOpen(true); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const run = async () => {
    if (!window.confirm(`Backup customers and merge ${a.duplicate_records_to_merge} duplicate record(s)? History is preserved.`)) return;
    setBusy(true);
    try { const r = await api.post("/customers/merge/run"); setRes(r.data); toast.success(`Merged. Backup: ${r.data.backup_collection}`); onDone?.(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" className="rounded-sm" onClick={analyze} disabled={busy} data-testid="merge-customers-btn"><GitMerge size={15} className="mr-1" /> Merge Duplicates</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="merge-customers-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Combined Customer Database</DialogTitle></DialogHeader>
          {a && (
            <div>
              <Row label="Existing customers (before merge)" value={a.existing_customers} testid="merge-existing" />
              <Row label="Imported customers" value={a.imported_customers} testid="merge-imported" />
              <Row label="Total records" value={a.total_records} testid="merge-total" />
              <Row label="Duplicate mobile numbers found" value={a.duplicate_mobiles} testid="merge-dups" />
              <Row label="Duplicate records to merge" value={a.duplicate_records_to_merge} testid="merge-dup-records" />
              <Row label="Final combined unique customers" value={res?.final_unique_customers ?? a.final_unique_customers} testid="merge-final" />
              {res && <p className="text-xs text-green-400 mt-3" data-testid="merge-result">Done — {res.merged_records} record(s) merged across {res.merged_mobiles} mobile(s). Backup: <b>{res.backup_collection}</b>. Archived copies in <b>{res.archive_collection}</b>.</p>}
              {!res && !!a.samples?.length && (
                <div className="mt-3 text-xs text-muted-foreground max-h-32 overflow-y-auto space-y-1">
                  {a.samples.map((s) => <div key={s.mobile}><b>{s.mobile}</b>: {s.records.map((r) => `${r.name || "(no name)"}${r.imported ? " [imported]" : ""} · ${r.visits} visits`).join(" | ")}</div>)}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {a && !res && a.duplicate_records_to_merge > 0 && <Button onClick={run} disabled={busy} data-testid="merge-run-btn" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]">{busy ? "Merging…" : "Backup & Merge"}</Button>}
            {a && !res && a.duplicate_records_to_merge === 0 && <span className="text-xs text-muted-foreground">No duplicates — database is already unique.</span>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
